import { queryOptions, useSuspenseQuery, type QueryClient } from "@tanstack/react-query";
import { z } from "zod";

import {
	apiPaths,
	emailConfirmationRequiredResponseSchema,
	mfaRequiredResponseSchema,
	passwordResetCodeSentResponseSchema,
	sessionOpenedResponseSchema,
	sessionSchema,
	signedInResponseSchema,
	signInResponseSchema,
	type ChangePasswordRequest,
	type CompleteNewPasswordRequest,
	type ConfirmOrganizationRequest,
	type ConfirmPasswordResetRequest,
	type CreateOrganizationRequest,
	type EmailConfirmationRequiredResponse,
	type MfaRequiredResponse,
	type PasswordResetCodeSentResponse,
	type RequestPasswordResetRequest,
	type Session,
	type SessionOpenedResponse,
	type SignedInResponse,
	type SignInRequest,
	type SignInResponse,
} from "@ravnar/api-contract";

import { ApiError, apiRequest } from "./client";

/**
 * Who is signed in, or null for a signed-out visitor. The API decides from its HttpOnly session cookies, which scripts
 * cannot read. Asked once and kept: signing in, creating an organization and signing out replace the answer.
 */
export const sessionQueryOptions = () =>
	queryOptions({
		queryKey: ["session"],
		queryFn: async ({ signal }): Promise<Session | null> => {
			try {
				return await apiRequest(apiPaths.session, sessionSchema, { signal });
			} catch (error) {
				if (error instanceof ApiError && error.status === 401) {
					return null;
				}
				throw error;
			}
		},
		staleTime: Infinity,
	});

/** The signed-in member and their organization, on pages behind the root route's guard, which makes sure there is one. */
export function useSession(): Session {
	const { data: session } = useSuspenseQuery(sessionQueryOptions());
	if (session === null) {
		throw new Error("useSession is only for pages that need a session");
	}
	return session;
}

/** Keeps a new session and drops everything cached for whoever was signed in before, maybe in another organization. */
export function startSession(queryClient: QueryClient, session: Session): void {
	queryClient.removeQueries();
	queryClient.setQueryData(sessionQueryOptions().queryKey, session);
}

/** Forgets the session and everything cached for it. */
export function endSession(queryClient: QueryClient): void {
	queryClient.removeQueries();
	queryClient.setQueryData(sessionQueryOptions().queryKey, null);
}

/**
 * Signs in and sets the session cookies, or asks a member who signed in with a temporary password to choose their own,
 * or asks someone who has not confirmed the organization they created for the code the API just emailed them again, or
 * asks a member with an authenticator app for its code.
 * Fails with 401 for a wrong email or password.
 */
export function signIn(request: SignInRequest): Promise<SignInResponse> {
	return apiRequest(apiPaths.session, signInResponseSchema, { method: "POST", body: request });
}

/**
 * Replaces a temporary password and signs in, or asks the member to set an authenticator app up when their
 * organization requires one and they have none. Fails with 401 once the 3 minutes to answer have passed.
 */
export function completeNewPassword(request: CompleteNewPasswordRequest): Promise<SessionOpenedResponse> {
	return apiRequest(apiPaths.sessionNewPassword, sessionOpenedResponseSchema, { method: "POST", body: request });
}

/**
 * Starts creating an organization: the API emails its owner a 6-digit code, and nothing else exists until
 * confirmOrganization sends that code. Fails with 409 for an email that already belongs to an organization.
 */
export function createOrganization(request: CreateOrganizationRequest): Promise<EmailConfirmationRequiredResponse> {
	return apiRequest(apiPaths.organizations, emailConfirmationRequiredResponseSchema, {
		method: "POST",
		body: request,
	});
}

/**
 * Confirms the emailed code, creates the organization with its creator as the owner and signs them in. Fails with 400
 * for a wrong or expired code, and with 409 when no organization waits for the email.
 */
export function confirmOrganization(request: ConfirmOrganizationRequest): Promise<SignedInResponse> {
	return apiRequest(apiPaths.organizationConfirmation, signedInResponseSchema, { method: "POST", body: request });
}

/** Signs out: the API records it, revokes the refresh token and clears the session cookies. */
export async function signOut(): Promise<void> {
	await apiRequest(apiPaths.session, z.undefined(), { method: "DELETE" });
}

/** Changes the signed-in member's password. Fails with 400 when the current password is wrong. */
export async function changePassword(request: ChangePasswordRequest): Promise<void> {
	await apiRequest(apiPaths.sessionPassword, z.undefined(), { method: "POST", body: request });
}

/**
 * Asks the API to email a code that resets a forgotten password. It answers the same way whether or not the address
 * has an account, so nothing here says who uses Ravnar.
 */
export function requestPasswordReset(request: RequestPasswordResetRequest): Promise<PasswordResetCodeSentResponse> {
	return apiRequest(apiPaths.sessionPasswordReset, passwordResetCodeSentResponseSchema, {
		method: "POST",
		body: request,
	});
}

/**
 * Sets a new password with the emailed code and signs in, or asks for the code from the member's authenticator app
 * first, or, when the organization requires a factor and the member has none, asks them to set one up. Fails with 400
 * for a wrong or expired code, or a password the user pool refuses.
 */
export function confirmPasswordReset(
	request: ConfirmPasswordResetRequest,
): Promise<SessionOpenedResponse | MfaRequiredResponse> {
	return apiRequest(
		apiPaths.sessionPasswordResetConfirm,
		z.discriminatedUnion("status", [...sessionOpenedResponseSchema.options, mfaRequiredResponseSchema]),
		{ method: "POST", body: request },
	);
}
